import { PanelLeft, PanelLeftClose } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { RosterFilterMenu } from './RosterFilterMenu';
import type { RosterFilters } from './rosterPositionFilter';
import type { IncidentPosition } from 'hooks/incident/useRoster';
import type { HwcgPeriodChoice } from 'components/IncidentConfig/roster/rosterPeriods';
import type { RosterOccupancyOption } from '../RosterOrgChart';

export const ROSTER_VISIBILITY_OPTIONS: {
  value: RosterOccupancyOption;
  label: string;
}[] = [
  { value: 'active', label: 'Current OP' },
  { value: 'draft', label: 'Next OP' },
  { value: 'empty', label: 'Empty Positions' },
];

export const SIDEBAR_STORAGE_KEY = 'roster-template-sidebar-collapsed';

export function readSidebarCollapsed(): boolean {
  try {
    return sessionStorage.getItem(SIDEBAR_STORAGE_KEY) === '1';
  } catch {
    return false;
  }
}

export function persistSidebarCollapsed(collapsed: boolean) {
  try {
    sessionStorage.setItem(SIDEBAR_STORAGE_KEY, collapsed ? '1' : '0');
  } catch {
    // Ignore private-mode / quota failures.
  }
}

type RosterChartChromeButtonsProps = {
  sidebarCollapsed: boolean;
  onSidebarCollapsedChange: (collapsed: boolean) => void;
  rosterFilters: RosterFilters;
  onRosterFiltersChange: (next: RosterFilters) => void;
  positions: IncidentPosition[];
  hideDraftOccupancy?: boolean;
  hwcgPeriodChoices?: readonly HwcgPeriodChoice[];
  iconOnlyFilter?: boolean;
  onPreviewIcs207?: () => void;
  /** Keep the ICS-207 control in the row without letting it run. */
  previewDisabled?: boolean;
  isExporting?: boolean;
  /** The plan already supplied a roster, so the template panel is not offered. */
  hideTemplateSidebar?: boolean;
};

export function RosterChartChromeButtons({
  sidebarCollapsed,
  onSidebarCollapsedChange,
  rosterFilters,
  onRosterFiltersChange,
  positions,
  hideDraftOccupancy = false,
  hwcgPeriodChoices,
  iconOnlyFilter = false,
  onPreviewIcs207,
  previewDisabled = false,
  isExporting = false,
  hideTemplateSidebar = false,
}: RosterChartChromeButtonsProps) {
  return (
    <>
      {hideTemplateSidebar ? null : (
        <Button
          variant="outline"
          size="sm"
          aria-label={
            sidebarCollapsed ? 'Show templates panel' : 'Hide templates panel'
          }
          title={
            sidebarCollapsed ? 'Show templates panel' : 'Hide templates panel'
          }
          onClick={() => onSidebarCollapsedChange(!sidebarCollapsed)}
        >
          {sidebarCollapsed ? (
            <PanelLeft className="tw:h-4 tw:w-4" />
          ) : (
            <PanelLeftClose className="tw:h-4 tw:w-4" />
          )}
          <span className="tw:ms-1">Templates</span>
        </Button>
      )}
      <RosterFilterMenu
        filters={rosterFilters}
        onFiltersChange={onRosterFiltersChange}
        positions={positions}
        hideDraftOccupancy={hideDraftOccupancy}
        hwcgPeriodChoices={hwcgPeriodChoices}
        iconOnly={iconOnlyFilter}
      />
      {onPreviewIcs207 ? (
        <Button
          variant="outline"
          size="sm"
          onClick={onPreviewIcs207}
          disabled={isExporting || previewDisabled}
        >
          {isExporting ? 'Preparing…' : 'Create ICS-207'}
        </Button>
      ) : null}
    </>
  );
}
